/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Grid,
  Checkbox,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { UUID } from "crypto";
import {
  useLazyGetPaymentDetailsQuery,
  useLazyGetPaymentMethodByIdQuery,
  usePostPaymentDetailsMutation,
  usePostPaymentMethodMutation,
  usePutPaymentDetailsMutation,
} from "@api/applications";
import { RootState } from "@store/store";
import { useLazyGetPaymentMethodQuery } from "@api/catalogs";
import CustomRadioGroup from "@components/CustomRadioGroup";
import { setPfaeTransactionalInfo } from "@store/slices/bofuSlicePfae";
import { InactivityHandler } from "@components/InactivityHandler";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, DynamicQuestionAnswerInput } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { buildProductRoute } from "@helpers/funciones";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";
import CustomDynamicQuestionField from "@components/CustomDynamicQuestionField";
import * as Sentry from "@sentry/react";
import { useLazyGetDynamicQuestionByApplicationQuery, usePostDynamicQuestionMutation } from "@api/dynamicQuestion";
import {
  buildDynamicQuestionAnswerItems,
  buildDynamicAnswersValidationSchema,
  isDynamicQuestionVisible,
  mapDynamicQuestionAnswerRecordsToFormValues,
} from "@helpers/dynamicQuestionsHelpers";

const EMPTY_UUID = "00000000-0000-0000-0000-000000000000";

const SolicitudCreditoPart2PF: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const companyData = useSelector((state: RootState) => state.bofuPfae.digital_file.pfae);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [triggerPaymentMethod] = usePostPaymentMethodMutation();
  const [triggerPaymentDetails] = usePostPaymentDetailsMutation();
  const [triggerPutPaymentDetails] = usePutPaymentDetailsMutation();
  const [getPaymentMethod] = useLazyGetPaymentMethodQuery();
  const [getDetalles] = useLazyGetPaymentDetailsQuery();
  const [getMetodoByApplication] = useLazyGetPaymentMethodByIdQuery();
  const [postDynamicQuestion] = usePostDynamicQuestionMutation();
  const [getDynamicQuestion] = useLazyGetDynamicQuestionByApplicationQuery();
  const [idOther, setIdOther] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [idDetalle, setIdDetalle] = useState<string | null>(null);
  const [paymentMethodList, setPaymentMethodList] = useState<Array<{ id: string; description: string }>>([]);

  const {
    transactional_info: { payment_methods, details },
  } = companyData;

  const { questions, isVisible, isRequired, requiredField } = useSolicitudCreditoForm("step_2");

  const validationSchema = yup.object().shape({
    paymentMethods: isRequired("paymentMethods", true)
      ? yup
          .array()
          .of(yup.string().required("Método de pago es requerido"))
          .min(1, "Selecciona al menos un método de pago")
          .required("Selecciona al menos un método de pago")
      : yup.array().notRequired(),
    otherPaymentMethod: yup.string().when("paymentMethods", {
      is: (methods: string[] = []) => methods.includes(idOther),
      then: (schema) =>
        isRequired("otherPaymentMethod", true)
          ? schema.required("Este campo es requerido para el método de pago 'Otro'")
          : schema.notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),
    advancePayments: requiredField(yup.string(), "advancePayments", true, "Campo requerido"),
    resourcesTypes: requiredField(yup.string(), "resourcesTypes", true, "Campo requerido"),
    dynamicAnswers: buildDynamicAnswersValidationSchema(questions),
  });

  const formik = useFormik({
    initialValues: {
      paymentMethods: (payment_methods || []).filter((item) => item !== EMPTY_UUID),
      otherPaymentMethod: "",
      advancePayments: details.advance_payments === undefined ? "" : details.advance_payments ? "Si" : "No",
      resourcesTypes: details.own_resources === undefined ? "" : details.own_resources ? "Propios" : "Tercero",
      // Respuestas de las preguntas dinámicas (step_2), indexadas por "code". Se precargan
      // desde el servicio real en el efecto de abajo (una vez que "questions" resuelve).
      dynamicAnswers: {} as Record<string, DynamicQuestionAnswerInput>,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const body = {
          payment_methods: values.paymentMethods.map((id) => ({
            payment_method_id: id,
            other_payment_method: id === idOther ? values.otherPaymentMethod : null,
          })),
        };
        await triggerPaymentMethod({
          solicitudId: applicationData.id,
          body,
        }).unwrap();

        // Si no hay configuración (o el catálogo no cargó) "questions" viene vacío y no se
        // ejecuta el POST, para no mandar un body vacío/inválido.
        if (questions.length > 0) {
          // No se mandan respuestas de preguntas que quedaron ocultas por "visible_when".
          const visibleQuestions = questions.filter((question) =>
            isDynamicQuestionVisible(question, values.dynamicAnswers),
          );
          const dynamicQuestionItems = buildDynamicQuestionAnswerItems(visibleQuestions, values.dynamicAnswers);

          if (dynamicQuestionItems.length > 0) {
            await postDynamicQuestion({
              solicitudId: applicationData.id,
              body: dynamicQuestionItems,
            }).unwrap();
          }
        }

        let detailsId = undefined;
        if (idDetalle) {
          const response = await triggerPutPaymentDetails({
            solicitudId: applicationData.id,
            detailId: idDetalle,
            body: {
              advance_payments: values.advancePayments === "Si",
              own_account: false,
              own_resources: values.resourcesTypes === "Propios",
            },
          }).unwrap();

          detailsId = response.data.id;
        } else {
          const response = await triggerPaymentDetails({
            solicitudId: applicationData.id,
            body: {
              advance_payments: values.advancePayments === "Si",
              own_account: false,
              own_resources: values.resourcesTypes === "Propios",
            },
          }).unwrap();

          detailsId = response.data.id;
        }

        dispatch(
          setPfaeTransactionalInfo({
            paymentMethods: formik.values.paymentMethods as UUID[],
            paymentDetailsId: detailsId as UUID,
            advancePayments: formik.values.advancePayments === "Si",
            resourcesTypes: formik.values.resourcesTypes === "Propios",
          }),
        );

        navigate(buildProductRoute(location.pathname, "solicitud_credito_3PF"));
      } catch (error) {
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    },
    validateOnMount: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (questions.length === 0) return;

    (async () => {
      try {
        const dynamicQuestionAnswers = await getDynamicQuestion(applicationData.id).unwrap();
        formik.setFieldValue(
          "dynamicAnswers",
          mapDynamicQuestionAnswerRecordsToFormValues(dynamicQuestionAnswers, questions),
        );
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart2PF-fetchDynamicQuestionAnswers",
          },
        });
      }
    })();
  }, [questions.length]);

  // Si una pregunta con "visible_when" deja de estar visible (ej. "pagos_anticipados" pasa de
  // "si" a "no"), se limpia su respuesta: si no, se guardaría una respuesta de una pregunta que
  // ya no se le mostró al cliente con esa condición.
  useEffect(() => {
    questions.forEach((question) => {
      if (!question.visible_when) return;

      const currentValue = formik.values.dynamicAnswers[question.code];
      const hasValue = Array.isArray(currentValue) ? currentValue.length > 0 : Boolean(currentValue);
      if (!hasValue) return;

      if (!isDynamicQuestionVisible(question, formik.values.dynamicAnswers)) {
        formik.setFieldValue(`dynamicAnswers.${question.code}`, question.type === "multiple_choice" ? [] : "");
      }
    });
  }, [formik.values.dynamicAnswers, questions]);

  const getDetallesWrapped = async () => {
    try {
      return await getDetalles({ solicitudId: applicationData.id }).unwrap();
    } catch (error) {
      return null;
    }
  };

  const fetchInitialData = async () => {
    try {
      const [detallesResponse, metodoResponse, paymentCatalog] = await Promise.all([
        getDetallesWrapped(),
        getMetodoByApplication({ solicitudId: applicationData.id }).unwrap(),
        getPaymentMethod().unwrap(),
      ]);

      const detallesData = detallesResponse?.data;
      const metodoData = metodoResponse.data;

      // buscar el id del metodo de pago "OTRO"
      const otherPaymentMethod = paymentCatalog.find((item) => item.code === "CPIOT");
      const paymentMethodIds = metodoData?.map((item) => item.payment_method?.id) ?? [];
      const paymentMethodInput = metodoData?.find((item) => item.payment_method?.id === otherPaymentMethod?.id);

      formik.setFieldValue("resourcesTypes", detallesData ? (detallesData.own_resources ? "Propios" : "Tercero") : "");
      formik.setFieldValue("advancePayments", detallesData ? (detallesData.advance_payments ? "Si" : "No") : "");
      formik.setFieldValue("paymentMethods", paymentMethodIds);
      formik.setFieldValue("otherPaymentMethod", paymentMethodInput?.other_payment_method || "");

      setIdDetalle(detallesData?.id ?? null);
      setIdOther(otherPaymentMethod?.id ?? "");
      setPaymentMethodList(paymentCatalog);

      // Varios setFieldValue seguidos disparan cada uno su propia validación async; al
      // resolver fuera de orden, una respuesta vieja puede pisar el resultado correcto y
      // dejar "isValid" en false aunque los valores ya estén completos. Se fuerza una
      // validación final ya con todos los campos poblados.
      setTimeout(() => {
        formik.validateForm();
      }, 200);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart2PF-fetchInitialData",
        },
      });
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleOnClickRestart = () => {
    navigate(buildProductRoute(location.pathname, "expediente_pf"));
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <Box ml={3} mr={3}>
          <Box textAlign={"center"} maxWidth="sm" mx="auto">
            <InactivityHandler />
            <Typography
              mt={4}
              mb={3}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
            >
              Solicitud de crédito
            </Typography>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={800}
              color={theme?.palette?.primary?.dark}
            >
              2{" "}
              <Typography
                component="span"
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
              >
                de 3
              </Typography>
            </Typography>
            <Typography
              my={3}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
            >
              Información transaccional
            </Typography>
            <form onSubmit={formik.handleSubmit} noValidate>
              {isVisible("paymentMethods") && (
                <Box textAlign={"left"} my={2}>
                  <Typography variant="body1">¿De qué forma realizarás tus pagos?</Typography>
                  <FormGroup>
                    {paymentMethodList.length > 0 ? (
                      paymentMethodList.map((paymentMethod, index) => (
                        <FormControlLabel
                          sx={{ color: theme?.palette?.primary?.dark }}
                          key={index}
                          control={
                            <Checkbox
                              key={paymentMethod.id}
                              name="paymentMethods"
                              checked={formik.values.paymentMethods.includes(
                                paymentMethod.id as `${string}-${string}-${string}-${string}-${string}`,
                              )}
                              onBlur={formik.handleBlur}
                              onChange={formik.handleChange}
                              value={paymentMethod.id}
                            />
                          }
                          label={paymentMethod.description}
                        />
                      ))
                    ) : (
                      <>
                        <Skeleton>
                          <FormControlLabel control={<Checkbox />} label={""}></FormControlLabel>
                        </Skeleton>
                        <Skeleton>
                          <FormControlLabel control={<Checkbox />} label={""}></FormControlLabel>
                        </Skeleton>
                        <Skeleton>
                          <FormControlLabel control={<Checkbox />} label={""}></FormControlLabel>
                        </Skeleton>
                        <Skeleton>
                          <FormControlLabel control={<Checkbox />} label={""}></FormControlLabel>
                        </Skeleton>
                      </>
                    )}
                  </FormGroup>
                  {/* Mostrar campo de texto si el método de pago "CPIOT" está seleccionado */}
                  {formik.values.paymentMethods.includes(idOther as UUID) && isVisible("otherPaymentMethod") && (
                    <TextField
                      fullWidth
                      id="otherPaymentMethod"
                      name="otherPaymentMethod"
                      label="Especifica el método de pago"
                      value={formik.values.otherPaymentMethod}
                      required={isRequired("otherPaymentMethod", true)}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.otherPaymentMethod && Boolean(formik.errors.otherPaymentMethod)}
                      helperText={formik.touched.otherPaymentMethod && formik.errors.otherPaymentMethod}
                      margin="normal"
                    />
                  )}
                </Box>
              )}
              {isVisible("advancePayments") && (
                <Box textAlign={"left"} my={2}>
                  <CustomRadioGroup
                    label={"¿Realizarás pagos anticipados?"}
                    name={"advancePayments"}
                    value={formik.values.advancePayments}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.advancePayments && Boolean(formik.errors.advancePayments)}
                    helperText={formik.touched.advancePayments && formik.errors.advancePayments}
                    options={[
                      {
                        label: "Si",
                        value: "Si",
                      },
                      {
                        label: "No",
                        value: "No",
                      },
                    ]}
                  />
                </Box>
              )}
              {isVisible("resourcesTypes") && (
                <Box textAlign={"left"} my={2}>
                  <CustomRadioGroup
                    label={"¿Los recursos son propios o de un tercero?"}
                    name={"resourcesTypes"}
                    value={formik.values.resourcesTypes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.resourcesTypes && Boolean(formik.errors.resourcesTypes)}
                    helperText={formik.touched.resourcesTypes && formik.errors.resourcesTypes}
                    options={[
                      {
                        label: "Propios",
                        value: "Propios",
                      },
                      {
                        label: "De un tercero",
                        value: "Tercero",
                      },
                    ]}
                  />
                </Box>
              )}

              {questions
                .filter((question) => isDynamicQuestionVisible(question, formik.values.dynamicAnswers))
                .map((question) => {
                  const touchedAnswer = formik.touched.dynamicAnswers?.[question.code];
                  const answerError = (formik.errors.dynamicAnswers as Record<string, string> | undefined)?.[
                    question.code
                  ];
                  return (
                    <Box textAlign={"left"} my={2} key={question.code}>
                      <CustomDynamicQuestionField
                        question={question}
                        value={
                          formik.values.dynamicAnswers[question.code] ?? (question.type === "multiple_choice" ? [] : "")
                        }
                        onChange={(newValue) => formik.setFieldValue(`dynamicAnswers.${question.code}`, newValue)}
                        onBlur={formik.handleBlur}
                        disabled={isLoading}
                        error={Boolean(touchedAnswer && answerError)}
                        helperText={touchedAnswer && answerError}
                      />
                    </Box>
                  );
                })}

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0", height: "100%" }}
                    onClick={handleOnClickRestart}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth type="submit" variant="default" disabled={!formik.isValid || isLoading}>
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SolicitudCreditoPart2PF;
